import 'reactflow/dist/style.css';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import ReactFlow, {
  Background, ControlButton, Controls, ReactFlowProvider,
} from 'reactflow';
import { ASYNC_STATUS } from 'utils/constants/flow.constant';
import { FlowSideBar } from 'pages/Flow/FlowSideBar/FlowSideBar';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import {
  checkFlowEdgesConnection,
  fetchFlowNodesCompleteDetails,
  nodeSizeChange,
  openSidePanel,
  updateFlowEdges,
  updateFlowNodesAction,
} from 'redux/actions/FlowAction';
import { edgeTypes } from 'pages/Flow/Edges/edgeTypes';
import { nodeTypes } from 'pages/Flow/Nodes/nodeTypes';
import { styled } from '@mui/material/styles';
import { useDeleteEdge, useDeleteNode } from 'api/flows';
import { useDispatch, useSelector } from 'react-redux';
import { useFlow } from 'pages/Flow/useFlow';


const DndStyled = styled('div')({
  flexDirection: 'row',
  display: 'flex',
  flexGrow: 1,
  height: '100%',
  '& .dndnode.reactflow-wrapper': {
    flexGrow: 1,
    height: '100%',
  },
  '& .dndnode.selectall': {
    marginTop: '10px',
  },
});
const ReactFlowRemoveChangeType = 'remove';

export const convertToReactFlowRemoveChange = (listToDelete) => (listToDelete.map((item) => ({
  id: item.id,
  type: 'remove',
})));

const MIN_RUN_DIALOG_ZOOM = 0.5;
const DEFAULT_RENDER_ZOOM = 0.2;

const FlowRender = ({
  crId, flowId, editMode, confirmAction,
  isCachedRun = false, isFlowRunDialog = false,
}) => {
  const dispatch = useDispatch();
  const {
    reactFlowWrapper,
    setReactFlowInstance,
    onDrop,
  } = useFlow();
  const { ID: orgId } = useSelector(activeOrgSelector);
  const nodeSize = useSelector(state => state.flow.nodeSize);
  const nodes = useSelector(state => state.flow.nodes);
  const edges = useSelector(state => state.flow.edges);
  const flow = useSelector(state => state.flow.flow);
  const cleanRoomFlow = useSelector(state => state.flow.cleanRoomFlow);
  const isFlowNodesAndEdgesLoaded = useSelector((state) => state.flow.flowNodesAndEdgesLoaded);
  const {
    status: nodeCompleteDetailsStatus,
  } = useSelector(state => state.flow.nodesCompleteDetails);
  const { mutate: deleteNode } = useDeleteNode(flowId, crId);
  const { mutate: deleteEdge } = useDeleteEdge(flowId, crId);
  const isFlowCachedMode = useSelector(state => state.flow.isFlowCachedMode);
  const shouldEnableInteraction = !isCachedRun && editMode;
  const isRenderInDialog = isFlowCachedMode || isFlowRunDialog;

  // Fetch all node complete details once when component mounts (uses redux saga to fetch)
  useEffect(() => {
    // This condition now correctly prevents a fetch if we've already failed
    const shouldFetch = (
      nodeCompleteDetailsStatus === ASYNC_STATUS.IDLE
      && isFlowNodesAndEdgesLoaded
      && crId
      && cleanRoomFlow.ID
    );

    if (shouldFetch) {
      dispatch(fetchFlowNodesCompleteDetails({
        orgId,
        crId,
        crFlowId: cleanRoomFlow.ID,
        flowVersion: flow.version,
      }));
    }
  }, [
    dispatch, crId, cleanRoomFlow.ID,
    orgId, flow.version, nodeCompleteDetailsStatus,
    isFlowNodesAndEdgesLoaded,
  ]);

  const nodeTypesMemo = useMemo(() => {
    if (!isFlowRunDialog) {
      return nodeTypes;
    }

    // Pass isFlowRunDialog to all node types when needed
    const enhancedNodeTypes = {};
    Object.keys(nodeTypes).forEach((key) => {
      const OriginalComponent = nodeTypes[key];
      // Preserve ref to maintain ReactFlow internals (forwardRef if exists)
      enhancedNodeTypes[key] = (props) => (
        <OriginalComponent {...props} isFlowRunDialog />
      );
    });
    return enhancedNodeTypes;
  }, [isFlowRunDialog]);
  const edgeTypesMemo = useMemo(() => (edgeTypes), []);

  const onConnect = useCallback((changes) => {
    dispatch(checkFlowEdgesConnection(changes));
  }, [dispatch]);

  const onNodesChange = useCallback((nodesChanges) => {
    let changes = nodesChanges;

    // Skip udating redux nodes state on node remove, handled in onNodesDelete method
    changes = nodesChanges.filter(({ type }) => (
      type !== ReactFlowRemoveChangeType
    ));

    dispatch(updateFlowNodesAction(changes));
  }, [dispatch]);

  const onEdgesChange = useCallback((edgeChanges) => {
    let changes = edgeChanges;

    // Skip udating redux edge state on edge remove, handled in onEdgesDelete method
    changes = edgeChanges.filter(({ type }) => (
      type !== ReactFlowRemoveChangeType
    ));

    dispatch(updateFlowEdges(changes));
  }, [dispatch]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodesDelete = useCallback((nodesToDelete) => {
    // If a node's ID starts with "Q_", it indicates that this node hasn't been submitted yet.
    // At this point, the node is only local to the flow canvas and hasn't been sent to the backend.
    // We don't need to fire the delete API call; we simply remove it from the local canvas.
    if (nodesToDelete[0].id.startsWith('Q_')) {
      return;
    }

    // Prompt user confirmation to delete
    confirmAction({
      type: 'NODE_DELETE',
      handle: (action) => {
        if (action.type === 'continue') {
          nodesToDelete.forEach((nodeToDelete) => {
            // Trigger API call per node to delete
            deleteNode(nodeToDelete.id);
          });
        }

        // Update local redux state for react flows
        const changes = convertToReactFlowRemoveChange(nodesToDelete);
        dispatch(updateFlowNodesAction(changes));
      },
    });
  }, [dispatch, deleteNode, confirmAction]);

  const onEdgesDelete = useCallback((edgesToDelete) => {
    // Prompt user confirmation when deleting edges of a deleted node
    confirmAction({
      type: 'EDGE_DELETE',
      handle: (action) => {
        if (action.type === 'continue') {
          edgesToDelete.forEach((edgeToDelete) => {
            // Trigger API call per edge to delete
            deleteEdge(edgeToDelete.id);
          });
        }

        // Update local redux state for react flows
        const changes = convertToReactFlowRemoveChange(edgesToDelete);
        dispatch(updateFlowEdges(changes));
      },
    });
  }, [dispatch, deleteEdge, confirmAction]);


  const onNodeDoubleClick = useCallback((e, node) => {
    // if it is a question node with data, we will open up the side drawer with information
    if (node.type === 'QUESTION' && node.data.ID) {
      dispatch(openSidePanel({ type: 'NODE', data: node }));
    }
  }, [dispatch]);

  const onNodeSizeChange = useCallback(() => {
    const newSize = nodeSize === 'MD' ? 'SM' : 'MD';
    dispatch(nodeSizeChange(newSize));
  }, [dispatch, nodeSize]);

  return (
    <DndStyled>
      <ReactFlowProvider>
        {shouldEnableInteraction && <FlowSideBar />}
        <div style={{ flexGrow: 1, overflow: 'hidden' }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onInit={(flowInstance) => {
              if (isRenderInDialog) {
                flowInstance.fitView({ padding: 0.2 });
                flowInstance.zoomTo(MIN_RUN_DIALOG_ZOOM);
              }
              else {
                flowInstance.fitView({ padding: 0.02 });
              }
              setReactFlowInstance(flowInstance);
            }}
            nodeTypes={nodeTypesMemo}
            edgeTypes={isRenderInDialog ? [] : edgeTypesMemo}
            minZoom={isFlowRunDialog ? MIN_RUN_DIALOG_ZOOM : DEFAULT_RENDER_ZOOM}
            {...(shouldEnableInteraction ? {
              onNodesDelete,
              onNodeDoubleClick,
              onEdgesChange,
              onEdgesDelete,
              onConnect,
              onDrop,
              onDragOver,
            } : {
              elementsSelectable: false,
              nodesConnectable: false,
              connectOnClick: false,
            })}
          >
            <Controls showInteractive={shouldEnableInteraction} position='top-right'>
              {!isCachedRun && (
                <ControlButton onClick={onNodeSizeChange}>
                  <ChangeCircleIcon />
                </ControlButton>
              )}
            </Controls>
            <Background />
          </ReactFlow>
        </div>
      </ReactFlowProvider>
    </DndStyled>
  );
};

export default FlowRender;
